import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import moment from "moment";
import { Controller, useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import FuseSplashScreen from "@fuse/core/FuseSplashScreen";
import CloseIcon from "@mui/icons-material/Close";
import utilityService from "@fuse/services/utilityService";
import { motion } from "framer-motion";
import {
	Avatar,
	Box,
	Switch,
	SwitchProps,
	TextareaAutosize,
	Typography,
	styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import CustomPrimaryButton from "@fuse/core/CustomPrimaryButton/CustomPrimaryButton";
import FieldLabel from "@fuse/core/FieldLabel/FieldLabel";
import _ from "lodash";
import { DatePicker } from "@mui/x-date-pickers";
import { BreedsType } from "src/app/main/client/addpet/types/BreedType";
import { IPetModel } from "@fuse/models";
import { showMessage } from "app/store/fuse/messageSlice";
import { useAppDispatch } from "app/store";
import adminService from "@fuse/services/adminService";
import { PetUpdateType } from "src/app/main/client/addpet/types/PetUpdateType";
import DeleteButton from "@fuse/core/DeleteButton/DeleteButton";
import TransferButton from "@fuse/core/TransferButton/TransferButton";

const BootstrapAutoComplete = styled(Autocomplete)(({ theme }: any) => ({
	"& .MuiAutocomplete-inputRoot": {
		borderRadius: 28,
		position: "relative",
		backgroundColor: theme.palette.background.paper,
		border: "2px solid #000",
		fontSize: 16,
		padding: "6px 16px",
		// transition: theme.transitions.create(['border-color', 'box-shadow']),
		boxShadow: "none",
		// Use the system font instead of the default Roboto font.
		fontFamily: [
			"Roboto Slab",
			"-apple-system",
			"BlinkMacSystemFont",
			'"Segoe UI"',
			"Roboto",
			'"Helvetica Neue"',
			"Arial",
			"sans-serif",
			'"Apple Color Emoji"',
			'"Segoe UI Emoji"',
			'"Segoe UI Symbol"',
		].join(","),
		"&:focus": {
			borderRadius: 28,
			borderColor: "#000",
			boxShadow: "none",
		},
	},
	".Mui-focused": {
		boxShadow: "none",
	},
}));

const BootstrapSwitch = styled((props: SwitchProps) => (
	<Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
	"& .MuiSwitch-switchBase": {
		transitionDuration: "300ms",
		"&.Mui-checked": {
			transform: "translateX(16px)",
			color: "#52BFF4",
			"& + .MuiSwitch-track": {
				backgroundColor: theme.palette.mode === "dark" ? "#9DC1Fc" : "#9DC1Fc",
				opacity: 1,
				border: 0,
			},
			"&.Mui-disabled + .MuiSwitch-track": {
				opacity: 0.5,
			},
		},
		"&.Mui-focusVisible .MuiSwitch-thumb": {
			color: "#52BFF4",
			border: "6px solid #52BFF4",
		},
		"&.Mui-disabled .MuiSwitch-thumb": {
			color:
				theme.palette.mode === "light"
					? theme.palette.grey[100]
					: theme.palette.grey[600],
		},
		"&.Mui-disabled + .MuiSwitch-track": {
			opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
		},
	},
	"& .MuiSwitch-thumb": {
		boxSizing: "border-box",
	},
	"& .MuiSwitch-track": {
		borderRadius: 26 / 2,
		backgroundColor: theme.palette.mode === "light" ? "#EEE" : "#EEE",
		opacity: 1,
		transition: theme.transitions.create(["background-color"], {
			duration: 500,
		}),
	},
}));

const schema = yup.object().shape({
	name: yup
		.string()
		.required("Pet Name is required")
		.min(2, "The Pet name must be at least 2 characters"),
	imageUrl: yup.string().nullable().optional(),
	microchip1: yup.string().optional(),
	speciesId: yup.string().nullable().optional(),
	petBreedList: yup.array().of(yup.string()).optional(),
	sex: yup.number().optional(),
	dob: yup.date().nullable().optional(),
	lastRabiesVaccine: yup.date().nullable().optional(),
	approximateWeight: yup.number(),
	spayedNeutered: yup.boolean(),
	isActive: yup.boolean(),
	notes: yup.string().nullable().optional(),
	isChipActive: yup.boolean(),
});

function AdminEditPetProfileForm({
	pet,
	allSpecies,
	loading,
	id,
	handleDelete,
	setLoading
}) {
	const petBreedList = pet.petBreedList.map((m) => m.id);
	const chip = pet.chips?.length > 0 ? pet.chips[0].chipId : "";
	const species = allSpecies.find((f) => f.id === pet.speciesId);

	const [Apiloading, setApiLoading] = useState(false);
	const [notesLength, setNotesLength] = useState(0);
	const [allBreeds, setBreeds] = useState([]);
	const [drag, setDrag] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [petData, setPetData] = useState(pet);

	function getPetImage(pet: IPetModel) {
		if (pet.image) {
			return `${process.env.VITE_APP_API_BASE_URL}Pet/pet-images/${pet.image}`;
		} else if (pet.species?.name) {
			return `assets/icons/pets/${pet.species.name.toLowerCase()}.png`;
		} else {
			return `assets/icons/pets/dog.png`;
		}
	}

	var sexList = [
		{ id: 0, name: "Unknown" },
		{ id: 1, name: "Male" },
		{ id: 2, name: "Female" },
	];
	let zero: number = 0;

	const methods = useForm({
		mode: "onChange",
		defaultValues: {
			name: pet.name,
			microchip1: chip,
			speciesId: pet.speciesId || "",
			petBreedList: petBreedList || "",
			dob: pet.dob ? moment(pet.dob).toDate() : null,
			sex: pet.sex || 0,
			lastRabiesVaccine: pet.lastRabiesVaccine
				? moment(pet.lastRabiesVaccine).toDate()
				: null,
			approximateWeight: pet.approximateWeight ?? 0,
			imageUrl: pet.imageUrl || "",
			spayedNeutered: pet.spayedNeutered ?? false,
			isActive: pet.isActive ?? false,
			notes: pet.notes ?? "",
			isChipActive: pet.isChipActive ?? false,
		},
		resolver: yupResolver(schema),
	});
	const {
		control,
		watch,
		trigger,
		reset,
		handleSubmit,
		formState,
		getValues,
		setValue,
		setError,
	} = methods;
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const isActive = watch("isActive");

	useEffect(() => {
		utilityService
			.getPetBreedsByPetId(pet.id)
			.then((res: any) => {
				if (res.result) {
					setBreeds(res.result);
				}
			})
			.catch((error) => {
				console.log(error);
			});
	}, []);

	const { errors, isValid, dirtyFields } = formState;

	const notes = watch("notes");
	const speciesId = watch("speciesId");

	useEffect(() => {
		setNotesLength(notes?.length);
	}, [notes]);

	const dragOver = (e) => {
		e.preventDefault();
		setDrag(true);
	};

	const dragEnter = (e) => {
		e.preventDefault();
		setDrag(true);
	};

	const dragLeave = (e) => {
		e.preventDefault();
		setDrag(false);
	};

	const fileDrop = (e, onChange) => {
		e.preventDefault();
		setDrag(false);
		const files = e.dataTransfer.files;
		if (files.length) {
			if (files.length > 1) {
				//TODO: Show Alert here
			}
			handleFile(files[0], onChange);
		}
	};

	const handleFile = (file, onChange) => {
		if (!file.type.startsWith("image/")) {
			alert("Only image files can be uploaded");
			return;
		}
		const reader: FileReader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === "string") {
				onChange(`data:${file.type};base64,${btoa(reader.result)}`);
			} else {
				// TODO: SHOW ERROR
			}
		};
		reader.onerror = () => {
			// TODO: SHOW ERROR
		};
		reader.readAsArrayBuffer(file);
	};
	async function onSubmit(data: any) {
		setLoading(true);
		await updatePet(data);
		setPetData(data);
		reset(data);
		setIsEditMode(false);
		setLoading(false);
	}

	function onSpeciesChange(speciesId) {
		setBreeds([]);
		setValue("petBreedList", []);

		setApiLoading(true);

		utilityService
			.getPetBreedsBySpeciesId(speciesId)
			.then((res: any) => {
				if (res.result) {
					setBreeds(res.result);
					setApiLoading(false);
				}
			})
			.catch((error) => {
				console.log(error);
				setApiLoading(false);
			});
	}

	function updatePet(data: PetUpdateType) {
		data.id = id;
		if (!data.dob) {
			data.dob = null;
		} else {
			data.dob = moment(data.dob).format("YYYY-MM-DD");
		}

		if (!data.lastRabiesVaccine) {
			data.lastRabiesVaccine = null;
		} else {
			data.lastRabiesVaccine = moment(data.lastRabiesVaccine).format(
				"YYYY-MM-DD"
			);
		}

		data.breeds = [];

		if (data.petBreedList?.length > 0) {
			data.petBreedList.forEach((f) => {
				data.breeds.push({
					id: f,
					name: f,
				});
			});
		}

		data.image = data.imageUrl?.indexOf(";") > 0 ? data.imageUrl : pet.image;

		adminService
			.updatePet(id, data)
			.then((res: any) => {
				let response = res?.result ? (res.result as IPetModel) : null;
				if (response?.id) {
					dispatch(
						showMessage({ message: "The pet has been saved successfully" })
					);
				}
				if (!pet.isActive) {
					navigate("/client/pet/checkout/" + id);
				}
			})
			.catch((_errors: string[]) => {
				if (_errors.length > 0) {
					let chipIdErrors = _errors.filter(
						(error) =>
							error.includes("chip number") || error.includes("microchip id")
					);
					if (chipIdErrors) {
						_errors.forEach((error) => {
							setError("microchip1", {
								type: "manual",
								message: error,
							});
						});
					} else {
						let apiError = _errors[0];
						if (apiError) {
							dispatch(
								showMessage({
									message: "Some error occurred, please contact our support.",
									variant: "error",
								})
							);
						}
					}
				}
			});
	}

	function handleDeleteClick() {
		handleDelete();
	}
	function handleTransferClick(email) {
		if (email) {
			adminService
				.transferPet(id, { Email: email })
				.then((res: any) => {
					let response = res?.result;
					if (response) {
						dispatch(showMessage({ message: response, variant: "success" }));
					}
				})
				.catch((_errors: string[]) => {
					if (_errors.length > 0) {
						let apiError = _errors[0];
						if (apiError) {
							dispatch(
								showMessage({
									message: "Some error occurred, please contact our support.",
									variant: "error",
								})
							);
						}
					}
				});
		}
	}

	return (
		<>
			{(loading || Apiloading) && <FuseSplashScreen />}
			<div className="flex flex-row sm:flex-row flex-1 w-full items-center justify-between space-y-8 sm:space-y-0 py-32 px-12 md:px-16">
				<div className="flex flex-col flex-1 items-center sm:items-start space-y-8 sm:space-y-0 w-full sm:max-w-full min-w-0">
					<div className="flex items-center max-w-full">
						<motion.div
							className="flex flex-col items-center sm:items-start min-w-0 mx-8 sm:mx-16"
							initial={{ x: -20 }}
							animate={{ x: 0, transition: { delay: 0.3 } }}
						>
							<Typography className="text-16 sm:text-20 truncate font-semibold">
								<span className="capitalize">{pet.name}</span>'s' Pet Profile
							</Typography>
						</motion.div>
					</div>
				</div>
				<div className="">
					<FieldLabel>Pet Enabled</FieldLabel>
					<BootstrapSwitch
						checked={isActive}
						onChange={(ev) => {
							setValue("isActive", ev.target.checked);
						}}
						aria-label="enabled or disabled?"
					/>
				</div>
			</div>
			{isEditMode ? (
				<div className="flex flex-col gap-[20px]">
					<form
						name="registerForm"
						noValidate
						className="flex w-full flex-col justify-center overflow-y-scroll gap-[20px]"
						id="signupform-form"
						onSubmit={handleSubmit(onSubmit)}
					>
						<div className="flex sm:flex-row flex-col w-full gap-[20px]">
							<div className="flex sm:justify-start align-start justify-center">
								<Controller
									control={control}
									name="imageUrl"
									render={({ field: { onChange, value } }) => (
										<Box
											className="relative flex items-center justify-center w-[200px] h-[200px] rounded-xl overflow-hidden border-dashed border-[2px] border-black"
											onDragOver={dragOver}
											onDragEnter={dragEnter}
											onDragLeave={dragLeave}
											onDrop={(e) => fileDrop(e, onChange)}
										>
											{/* <div className="absolute inset-0 bg-black bg-opacity-50 z-10" /> */}
											<div className="absolute inset-0 flex items-center justify-center z-20 w-full h-full cursor-pointer p-[40px_20px]">
												<div>
													<label
														htmlFor="pet-image"
														className="flex cursor-pointer"
													>
														<input
															accept="image/*"
															className="hidden"
															id="pet-image"
															type="file"
															onChange={async (e) => {
																function readFileAsync() {
																	return new Promise((resolve, reject) => {
																		const file = e?.target?.files?.[0];
																		if (!file) {
																			return;
																		}
																		const reader: FileReader = new FileReader();

																		reader.onload = () => {
																			if (typeof reader.result === "string") {
																				resolve(
																					`data:${file.type};base64,${btoa(
																						reader.result
																					)}`
																				);
																			} else {
																				reject(
																					new Error(
																						"File reading did not result in a string."
																					)
																				);
																			}
																		};
																		reader.onerror = reject;
																		reader.readAsBinaryString(file);
																	});
																}
																const newImage = await readFileAsync();
																onChange(newImage);
															}}
														/>
														<FuseSvgIcon className="text-white opacity-10 text-opacity-0 w-full h-full">
															heroicons-outline:camera
														</FuseSvgIcon>
													</label>
												</div>
												<div className="absolute top-10 right-6 cursor-pointer">
													<CloseIcon
														className="text-32"
														onClick={() => {
															onChange("");
														}}
													/>
												</div>
											</div>
											<Avatar
												sx={{
													backgroundColor: "background.default",
													color: "text.secondary",
													borderRadius: 0,
												}}
												className="object-cover w-full h-full text-center text-[12px] font-700 leading-[24px]"
												src={value}
												alt={yup?.Schema?.name}
											>
												Drag and drop or click to upload your profile photo
											</Avatar>
										</Box>
									)}
								/>
							</div>
							<div className="flex flex-col w-full gap-[20px]">
								<div className="flex sm:flex-row flex-col w-full gap-[20px]">
									<div className="flex w-full flex-col sm:gap-[8px] gap-[4px]">
										<FieldLabel>Pet Name</FieldLabel>
										<Controller
											name="name"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													className="mb-20 bg-white rounded-24 h-[44px] sm:h-46px text-14px sm:text-16px font-400 sm:font-700 leading-22px sm:leading-22px text-black placeholder-black"
													required
													aria-label="Pet Name"
													id="name"
													variant="outlined"
													fullWidth
													sx={{ border: "solid 2px black" }}
													error={!!errors.name}
													helperText={errors?.name?.message as string}
												/>
											)}
										/>
									</div>
									<div className="flex w-full flex-col  sm:gap-[8px] gap-[4px]">
										<FieldLabel>Birth Date</FieldLabel>
										<Controller
											name="dob"
											control={control}
											render={({ field }) => (
												<DatePicker
													{...field}
													className="mb-20 bg-white rounded-24 h-[44px] sm:h-46px text-14px sm:text-16px font-400 sm:font-700 leading-22px sm:leading-22px text-black placeholder-black"
													sx={{ border: "solid 2px black" }}
												/>
											)}
										/>
									</div>
								</div>

								<div className="flex sm:flex-row flex-col w-full gap-[20px]">
									<div className="flex w-full flex-col  sm:gap-[8px] gap-[4px]">
										<FieldLabel>Microchip Number(s)</FieldLabel>
										<Controller
											name="microchip1"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													className="mb-20 bg-white rounded-24 h-[44px] sm:h-46px text-14px sm:text-16px font-400 sm:font-700 leading-22px sm:leading-22px text-black placeholder-black"
													id="chipId"
													variant="outlined"
													fullWidth
													sx={{ border: "solid 2px black" }}
													error={!!errors?.microchip1}
													helperText={errors?.microchip1?.message as string}
												/>
											)}
										/>
									</div>
									<div className="flex w-full flex-col  sm:gap-[8px] gap-[4px] items-center">
										<FieldLabel>Chip Enabled</FieldLabel>
										<Controller
											name="isChipActive"
											control={control}
											render={({ field: { onChange, value } }) => (
												<div className="flex justify-end items-center h-full w-full">
													<BootstrapSwitch
														checked={value}
														onChange={(ev) => {
															onChange(ev.target.checked);
														}}
														aria-label="enabled or disabled?"
													/>
												</div>
											)}
										/>
									</div>
								</div>

								<div className="flex sm:flex-row flex-col w-full gap-[20px]">
									<div className="flex w-full flex-col  sm:gap-[8px] gap-[4px]">
										<FieldLabel>Sex</FieldLabel>
										<Controller
											name="sex"
											control={control}
											defaultValue={zero}
											render={({ field: { onChange, value } }) => (
												<BootstrapAutoComplete
													// onChange={onChange}
													onChange={(
														event: any,
														newValue: { id: string; name: string }
													) => {
														onChange(newValue?.id);
													}}
													value={value || null}
													options={sexList}
													// groupBy={(option: any) => option?.country}
													getOptionLabel={(option: any) =>
														isNaN(option)
															? option
																? option?.name
																: ""
															: sexList.find((c) => c?.id == option)?.name ?? ""
													}
													renderInput={(params: any) => {
														return (
															<TextField
																{...params}
																placeholder="Please Select"
																id="sex"
																variant="standard"
																fullWidth
																InputProps={{
																	...params.InputProps,
																	disableUnderline: true,
																}}
																error={!!errors.sex}
																helperText={errors?.sex?.message as string}
															/>
														);
													}}
												/>
											)}
										/>
									</div>
									<div className="flex w-full flex-col  sm:gap-[8px] gap-[4px]">
										<FieldLabel>Last Rabies Vaccine</FieldLabel>
										<Controller
											name="lastRabiesVaccine"
											control={control}
											render={({ field }) => (
												<DatePicker
													{...field}
													className="mb-20 bg-white rounded-24 h-[44px] sm:h-46px text-14px sm:text-16px font-400 sm:font-700 leading-22px sm:leading-22px text-black placeholder-black"
													sx={{ border: "solid 2px black" }}
												/>
											)}
										/>
									</div>
								</div>

								<div className="flex sm:flex-row flex-col w-full gap-[20px]">
									<div className="flex w-full flex-col  sm:gap-[8px] gap-[4px]">
										<FieldLabel>Species</FieldLabel>
										<Controller
											name="speciesId"
											control={control}
											render={({ field: { onChange, value } }) => (
												<BootstrapAutoComplete
													// onChange={onChange}
													onChange={(
														event: any,
														newValue: { id: string; name: string }
													) => {
														onChange(newValue?.id);
														if (newValue?.id) {
															onSpeciesChange(newValue?.id);
														}
													}}
													value={value || null}
													options={allSpecies}
													// groupBy={(option: any) => option?.country}
													getOptionLabel={(option: any) =>
														typeof option == "object"
															? option
																? option?.name
																: ""
															: allSpecies.find((c) => c?.id == option)?.name ??
															""
													}
													renderInput={(params: any) => {
														return (
															<TextField
																{...params}
																placeholder="Please Select"
																id="speciesId"
																variant="standard"
																fullWidth
																InputProps={{
																	...params.InputProps,
																	disableUnderline: true,
																}}
																error={!!errors.speciesId}
																helperText={errors?.speciesId?.message as string}
															/>
														);
													}}
												/>
											)}
										/>
									</div>
									<div className="flex w-full flex-col  sm:gap-[8px] gap-[4px]">
										<FieldLabel>Breed(s) - Add up to 3</FieldLabel>
										<Controller
											name="petBreedList"
											control={control}
											render={({ field: { onChange, value } }) => (
												<BootstrapAutoComplete
													multiple
													freeSolo
													options={allBreeds}
													className="mb-20"
													getOptionLabel={(option: any) => option?.name || ""}
													onChange={(event, newValue: any) => {
														if (newValue.length <= 3) {
															onChange(newValue.map((item) => item?.id));
														}
													}}
													value={
														value
															? ((value as any[]).map((id) =>
																_.find(allBreeds, { id })
															) as BreedsType)
															: ([] as BreedsType)
													}
													renderInput={(params) => (
														<TextField
															{...params}
															placeholder="Select Breeds"
															variant="outlined"
															InputLabelProps={{
																shrink: true,
															}}
															error={!!errors.petBreedList}
															helperText={errors?.petBreedList?.message as string}
														/>
													)}
												/>
											)}
										/>
									</div>
								</div>
								<div className="flex sm:flex-row flex-col w-full gap-[20px]">
									<div className="flex w-full flex-col  sm:gap-[8px] gap-[4px]">
										<FieldLabel>Approximate Weight (lbs)</FieldLabel>
										<Controller
											name="approximateWeight"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													type="number"
													inputProps={{
														step: 1.0, // This allows decimal inputs
													}}
													className="mb-20 bg-white rounded-24 h-[44px] sm:h-46px text-14px sm:text-16px font-400 sm:font-700 leading-22px sm:leading-22px text-black placeholder-black"
													required
													id="approximateWeight"
													variant="outlined"
													fullWidth
													sx={{ border: "solid 2px black" }}
													error={!!errors?.approximateWeight}
													helperText={
														errors?.approximateWeight?.message as string
													}
												/>
											)}
										/>
									</div>
								</div>

								<div className="flex sm:w-1/2 w-full items-center">
									<Controller
										name="spayedNeutered"
										control={control}
										render={({ field: { onChange, value } }) => (
											<div className="flex justify-between items-center h-full w-full">
												<FieldLabel>Spayed/neutered</FieldLabel>
												<BootstrapSwitch
													checked={value}
													onChange={(ev) => {
														onChange(ev.target.checked);
													}}
													aria-label="spayed or netered?"
												/>
											</div>
										)}
									/>
								</div>
								<div className="flex w-full flex-col sm:gap-[8px] gap-[4px]">
									<FieldLabel>Pet Notes</FieldLabel>
									<Controller
										name="notes"
										control={control}
										render={({ field }) => (
											<TextareaAutosize
												{...field}
												minRows={5}
												maxRows={5}
												onChange={(event) => {
													if (event.target.value?.length <= 200) {
														field.onChange(event);
													}
												}}
												className=" bg-white rounded-[20px] p-[8px] border-[2px] border-black h-[44px] sm:h-[102px] text-14px sm:text-[16px] font-400 sm:font-400 leading-22px sm:leading-[24px] text-black placeholder-black"
												id="notes"
											/>
										)}
									/>
									<p className="text-400 font-[14px] leading-[17.5px] w-full text-end pr-10">
										{notesLength}/200
									</p>
								</div>
							</div>
						</div>
						<div className="flex sm:justify-between justify-center">
							<DeleteButton
								onDelete={handleDeleteClick}
								recordId={0}
								buttonText="Remove"
								buttonClass="border border-red-500"
								dialogText="Are you sure? This action cannot be reversed."
							/>
							<TransferButton
								onTransfer={handleTransferClick}
								buttonText="Transfer"
								buttonClass="border border-red-500"
								dialogText="Are you sure you want to transfer this pet?"
							/>
							<CustomPrimaryButton
								onClick={(e) => {
									trigger();
								}}
							>
								Save
							</CustomPrimaryButton>
							<Button
								className="font-semibold rounded-8 mx-10 mt-8 px-10 py-20"
								onClick={() => {
									setIsEditMode(false);
									reset();
								}}
							>
								Cancel
							</Button>
						</div>
					</form>
				</div>
			) : (
				<div className="flex flex-col md:flex-row gap-6 p-6 border rounded-xl shadow-sm bg-white w-full">
					{/* Pet Image */}
					<div className="flex-shrink-0 flex flex-col items-center">
						<Avatar
							src={getPetImage(pet)}
							alt={petData.name}
							sx={{
								width: 160,
								height: 160,
								borderRadius: 4,
								border: "2px solid #000",
							}}
						/>
						<h2 className="text-2xl md:text-3xl font-bold mt-3 text-center md:text-left">
							{petData.name}
						</h2>
					</div>

					{/* Pet Info */}
					<div className="flex flex-col gap-6 flex-1">
						<div className="flex flex-col md:flex-row md:items-center gap-4 mb-6 md:pl-10">
							<span className="text-lg font-bold md:text-xl font-medium text-gray-900">
								Microchip: {chip || "-"}
							</span>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 md:pl-10">
							<div className="flex gap-2">
								<span className="font-700">Birth Date:</span>
								<span>{petData.dob ? moment(petData.dob).format("MM/DD/YYYY") : "-"}</span>
							</div>
							<div className="flex gap-2">
								<span className="font-700">Last Rabies Vaccine:</span>
								<span>{petData.lastRabiesVaccine ? moment(petData.lastRabiesVaccine).format("MM/DD/YYYY") : "-"}</span>
							</div>
							<div className="flex gap-2">
								<span className="font-700">Species:</span>
								<span>{species?.name || "-"}</span>
							</div>
							<div className="flex gap-2">
								<span className="font-700">Breed(s):</span>
								<span>{petBreedList.map((id) => allBreeds.find((b) => b.id === id)?.name).join(", ") || "-"}</span>
							</div>
							<div className="flex gap-2">
								<span className="font-700">Sex:</span>
								<span>{sexList.find((s) => s.id === petData.sex)?.name || "-"}</span>
							</div>
							<div className="flex gap-2">
								<span className="font-700">Spayed/Neutered:</span>
								<span>{petData.spayedNeutered ? "Yes" : "No"}</span>
							</div>
							<div className="flex gap-2">
								{/* <span className="font-700">Share Contact Info:</span>
								<span>{petData.shareContactInfo ? "Yes" : "No"}</span> */}
							</div>
							<div className="flex gap-2">
								<span className="font-700">Approx. Weight:</span>
								<span>{petData.approximateWeight || "-"} lbs</span>
							</div>
						</div>

						<div className="mt-6 md:pl-10">
							<span className="font-700">Notes:</span>
							<p className="ml-2 mt-2 text-gray-700">{petData.notes || "-"}</p>
						</div>

						<div className="mt-6 flex justify-end pr-4">
							<Button
								variant="contained"
								color="primary"
								onClick={() => setIsEditMode(true)}
							>
								Edit
							</Button>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

export default AdminEditPetProfileForm;
